import { useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { BadgeCheck, ExternalLink, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

export default function QuoteCheckout({ open, onOpenChange, config, bouquetName, shareId, spec, onPaid }) {
  const [paid, setPaid] = useState(null);

  const handleClose = (v) => {
    if (!v) setPaid(null);
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="quote-checkout-dialog">
        {paid ? (
          <div className="text-center py-2">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <BadgeCheck className="w-7 h-7 text-primary" />
            </div>
            <DialogTitle className="font-serif-display text-3xl">Your quote is unlocked! 🌸</DialogTitle>
            {paid.price && (
              <div className="mt-5 text-left bg-muted rounded-2xl p-5" data-testid="unlocked-price">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Your bouquet price</span>
                  <span className="font-serif-display text-3xl text-primary">${paid.price.total.toFixed(2)}</span>
                </div>
                <div className="space-y-1">
                  {paid.price.lines.map((l, i) => (
                    <div key={i} className="flex justify-between text-sm text-muted-foreground">
                      <span>{l.label}</span><span>${l.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
              {paid.florist_name} will follow up about
              <span className="font-medium text-foreground"> "{bouquetName}"</span>. A copy has been emailed to you.
            </p>
            <a
              href={paid.florist_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="florist-link"
              className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all"
            >
              Visit {paid.florist_name} <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-secondary" /> Unlock your bouquet price
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed pt-1">
                Pay a one-time
                <span className="font-medium text-foreground"> ${config?.quote_fee?.toFixed(2)} </span>
                fee to reveal your bouquet's full price and receive an official quote from
                <span className="font-medium text-foreground"> {config?.florist_name}</span>.
                <span className="block mt-2 text-primary font-medium">100% obligation-free — no commitment, no pushy follow-ups.</span>
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4">
              {config?.paypal_configured ? (
                <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "USD" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "pill", color: "gold" }}
                    createOrder={async () => {
                      const r = await api.post("/paypal/create-order", {
                        bouquet_name: bouquetName, share_id: shareId, spec,
                      });
                      return r.data.id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const r = await api.post(`/paypal/capture-order/${data.orderID}`);
                        setPaid(r.data);
                        onPaid?.(r.data.price);
                        toast.success("Payment complete! Price unlocked. 🌸");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Payment could not be completed.");
                      }
                    }}
                    onError={() => toast.error("Something went wrong with PayPal.")}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground text-center" data-testid="paypal-not-configured">
                  PayPal checkout isn't configured yet. Please add your PayPal credentials to enable payments.
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
